import { NODE_TYPES } from "@via/utils/constants";
import {
  CreateProcessParameterRequestBody,
  EntityTagResponse,
  InputType,
  UpsertPipelineRequestBody,
  type McpToolCallInfo,
} from "common";
import { type MutableRefObject, type ReactNode, type RefCallback } from "react";

export interface Pagination<T> {
  total: number;
  skip: number;
  take: number;
  data: T[];
}

export interface LogsInterface {
  name: string;
  content: string;
}

export interface ActivityInterface {
  projects: any[];
  pipelines: any[];
  apps: any[];
}

export interface CollectionInterface {
  id: number;
  name: string;
  fileCount: number;
  createdAt: string;
  modifiedAt: string;
}

export interface PipelineInterface {
  id: number;
  name: string;
  summary: string;
  version: string;
  owner: string;
  createdAt: Date;
  modifiedAt: Date;
  pipelineType: string;
  pin: string;
  pinOrder: number;
  aiEntity?: boolean;
  tags?: EntityTagResponse[];
  ownerName?: string;
}

export interface ProjectShareOption {
  publicShare: boolean;
}

export interface ProcessDataInterface {
  id: string;
  name: string;
  rev_comment?: string;
  date_modified: string | Date;
  rev_id: number;
  summary: string;
}

export interface PipelineListProps {
  pipelines: PipelineInterface[];
}

export interface ParameterInterface {
  id: number;
  name: string;
  fileType?: string;
  qualifier: string;
  set?: string;
}

export interface LoginPayload {
  username: string;
  password: string;
  identityType: number | null;
  redirectUri?: string | null;
  remember?: boolean;
}

export interface SignUpPayload {
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
  redirectUri?: string | null;
  tos?: boolean;
}

export interface OutputParameterPayload {
  label: string;
  pubWeb?: object;
  pubWebApp?: {
    app?: string[];
    extension?: string;
    launchRunCheck?: boolean;
    launchRunId?: string;
    launchRunParam?: string;
    mountAppFile?: string;
  };
  pubDmeta?: {
    filename: string;
    feature?: string;
  };
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface UpdateInputParameterPayload {
  label?: string;
  defVal: string;
  dropDown: string;
  showSett: string;
  inDescOpt: string;
  inLabelOpt: string;
}

export interface EditDisplayNamePayload {
  label: string;
}

export interface ActivityRunResponse {
  id: number;
  name: string;
  status: string;
  username: string;
  dateCreated: Date;
  dateModified: Date;
  pipelineId: number;
  pipelineName: string;
  projectId: number;
  summary: string;
  pipelineVersion: string;
  dateCreatedLastRun: Date;
  tags?: EntityTagResponse[];
  /** Whether this run's subject is a real pipeline or a single process launched on its own.
   *  Undefined means "pipeline", which is how every payload from an older server reads.
   *  RunPipelineLink uses it to decide whether the pipeline name is a link. */
  subjectType?: "pipeline" | "process";
  /** Set only when subjectType is "process". The process the run was created from. */
  sourceProcessId?: number | null;
}

export interface VerifyPasswordResetTokenPayload {
  token: string;
}

export interface ResetPasswordPayload {
  password: string;
  confirmPassword: string;
}

export interface SetNewPasswordPayload {
  token: string | null;
  newPassword: string;
  redirectUri?: string;
}

export interface TableContextMenuInterface {
  name: string;
  href: string;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  /** Shown on hover, e.g. why an option is disabled. */
  title?: string;
}

export interface SelectOption {
  label: string | undefined;
  value: string | number | boolean | null;
  icon?: string;
  revComment?: string;
  version?: string;
  createdAt?: string | Date;
  dateCreated?: string | Date;
  metadata?: any;
  description?: string | null;
}

export const isSelectOption = (
  option: SelectOption | readonly SelectOption[] | null
): option is SelectOption => {
  return Boolean(option) && !Array.isArray(option);
};

export interface Tab<T extends string = string> {
  id?: number;
  name: T;
  customTitle?: ReactNode;
  href?: string;
  current?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  /** An extra per-row action a vertical tab renders beside its delete icon. */
  trailing?: ReactNode;
}

export interface IDataSources {
  id: string;
  name: string;
  description?: string;
  componentName: string;
  alt: string;
  category:
    | "cloud-storage"
    | "apis"
    | "ssh-data"
    | "token-based"
    | "remote-storage"
    | "remote-storage-oauth";
  icon?: any;
  href: string;
  comingSoon?: boolean;
  color?: string;
}

// We're extending the standard "Error" interface from react-query for use cases where we want to send a notification.
// `response` is optional because a network failure rejects without one (#930); read it through
// getErrorResponseMessage (utils/helpers) rather than `error.response.data`.
export interface ErrorNotification extends Error {
  response?: {
    data: any;
  };
}

export interface EmpireCardInterface {
  title: string;
  href: string;
  details: string;
  icon?: any;
}

export interface UserGroupInterface {
  id: number;
  name: string;
}

export interface CollectionItem {
  collectionId: string;
  collectionName: string;
  collectionType?: InputType;
  value?: string;
}

export interface ManualFilePathItem {
  value: string;
}

export interface TooltipInput {
  fileType: string;
}
export interface NodeData {
  inputHandles: string[];
  tooltips: {
    inputs: TooltipInput[];
  };
}

export type AdvancedFilesInterface =
  | {
      filename: string;
      text: string | undefined;
    }
  | any;

export type AnyRef = MutableRefObject<any> | RefCallback<any> | null;

export interface TreeDataScriptInterface extends TreeData {
  path?: string;
  text?: string;
}

export interface TreeData {
  id: string;
  name: string;
  children?: TreeData[];
}

export interface TreeStructureProps {
  tree: TreeDataScriptInterface[];
  selection?: string | null;
  onSelect: (item: TreeDataScriptInterface) => void;
}

export interface EditProcessFormRequestBody extends CreateProcessParameterRequestBody {
  name: string;
  description: string;
  menuGroupId: number;
  inputParameters: CreateProcessParameterRequestBody[]; // Can be empty
  outputParameters: CreateProcessParameterRequestBody[]; // Can be empty
  globalParameterId: number;
  parameterId: number;
  displayName: string;
  originalName: string;
  operator: string;
  operatorContent: string;
  optional: boolean;
  test: string;
  /**
   * Output parameters only. Overrides the publish pattern the run generator would otherwise
   * derive from displayName. Undefined means "leave whatever is stored alone".
   */
  regEx?: string;
  fileType: string;
  qualifier: string;
  revisionComment?: string;
}

export type CredentialKeys = {
  amazonCredentials?: {
    name: string;
    hide: boolean;
    amzAccKey: string;
    amzSucKey: string;
    amzDefReg: string;
  };
  googleCredentials?: {
    name: string;
    projectId: string;
    keyName: string;
  };
  ssh?: {
    id?: number;
    name: string;
    hide: boolean;
    checkOurKey?: string;
    checkUserKey?: string;
    privateKey?: string;
    publicKey?: string;
    pendingKeyId?: string;
    ownerId?: number;
  };
};

export type NewPipelineRequestBody = {
  name: string;
  menuGroup: number | null;
  summary: string;
};

export interface PipelineDraftNode {
  xCoordinate: number;
  yCoordinate: number;
  param: string | number;
  nodeName: string;
  nodeSettings: Record<string, unknown>;
  nodeType?: string;
  inputHandles?: unknown[];
  outputHandles?: unknown[];
  [key: string]: unknown;
}

// Extend the Upsert type since we append some custom fields client side to help with managing permissions, etc
export interface PipelineDraftData extends UpsertPipelineRequestBody {
  id?: number;
  nodes: Record<string, PipelineDraftNode>;
  isEditingAllowed: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  writeGroupIds: number[] | [];
  rootId?: number;
  [key: string]: unknown;
}

export interface ScriptPipeConfig {
  scriptPipeConfig: string;
  scriptPipeHeader?: string;
  scriptPipeFooter?: string;
}

export enum DataSourceComponentView {
  FileOrganizer = "FileOrganizer",
  FolderBrowser = "FolderBrowser",
}

export interface BaseNodeInterface {
  id: string;
  name: string;
  type: string;
  position: {
    x: number;
    y: number;
  };
  data: {
    label: string;
    type: string;
    className: string;
    inputHandles: string[];
    outputHandles: string[];
    tooltips: {
      inputs: TooltipInput[];
      outputs: TooltipInput[];
    };
  };
}
export interface ProcessNodeInterface extends BaseNodeInterface {
  type: NODE_TYPES.ProcessNode;
  data: BaseNodeInterface["data"] & {
    processId: number;
  };
}
export interface ModuleNodeInterface extends BaseNodeInterface {
  type: "moduleNode";
  data: BaseNodeInterface["data"] & {
    pipelineId: number;
  };
}

export interface InputOutputNodeInterface extends BaseNodeInterface {
  type: "inputNode" | "outputNode";
  data: BaseNodeInterface["data"] & {
    parameterId: string;
  };
}

export type NodeInterface =
  | ProcessNodeInterface
  | ModuleNodeInterface
  | InputOutputNodeInterface;

export interface EdgeInterface {
  id: string;
  type?: string;
  node: string;
  parameterId?: number;
  pipelineId?: number;
  processId?: number;
  source: string | null;
  target: string | null;
  sourceHandle: string | null;
  targetHandle: string | null;
}

export interface PipelineDetailInterface {
  pipeline: PipelineInterface;
  update: Record<string, string | boolean>;
}

export interface BaseSpaceConnectionCredentials {
  tokenId: number;
}

export interface BaseSpaceProjectListParams extends BaseSpaceConnectionCredentials {}

export interface BaseSpaceDatasetListParams {
  tokenId: number;
  projectId: string;
}

export interface BaseSpaceFileListParams {
  tokenId: number;
  datasetId: string;
}

// Generic operator map type: keys are filter keys, values are MongoDB operators as strings
export interface OperatorMap {
  [key: string]: string;
}

// AI Insight Conversation types
export interface Conversation {
  id: string; // UUID (or temp-* prefix for pending conversations)
  title: string | null; // Auto-generated, nullable for new conversations
  conversationHistory: Array<{
    role: "user" | "assistant" | "system";
    content: string;
    mcpToolCall?: McpToolCallInfo;
  }>;
  createdAt: number; // Timestamp
  updatedAt: number; // Timestamp
  contextRoutePattern: string; // e.g., "/runs/:id"
  contextName: string; // e.g., "Run Logs"
  pageUrl?: string | null; // Full page URL where conversation was created
  tokenId?: number; // Optional token ID
  model?: string; // Optional model
  contextFiles?: Array<{
    // Files used in this conversation (for file-based contexts)
    filePath: string;
    runUUID: string;
    reportName?: string;
    reportExtension?: string;
  }>;
  isPending?: boolean; // true if not yet saved to DB (temporary conversation)
  isPinned?: boolean; // true if user pinned the conversation
}
